import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, FileText, CheckCircle, Clock, X, Eye, File, Play } from 'lucide-react';

const DocumentUpload = ({ uploadedFiles = [], onFilesUploaded, onProcessingChange, onShowPreview }) => {
  const [, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef();

  const requiredDocuments = [
    { 
      name: 'Estado de Situación Financiera', 
      key: 'balance', 
      icon: FileText,
      description: 'Balance General (PDF)',
      uploaded: false
    },
    { 
      name: 'Estado de Resultados', 
      key: 'income', 
      icon: FileText,
      description: 'Estado de Pérdidas y Ganancias (PDF)',
      uploaded: false
    },
    { 
      name: 'Notas Financieras', 
      key: 'notes', 
      icon: File,
      description: 'Notas a los Estados Financieros (PDF)',
      uploaded: false
    }
  ];

  const handleFiles = async (files, targetKey = null) => {
    const fileArray = Array.from(files);
    const processedFiles = [];

    setIsProcessing(true);
    onProcessingChange && onProcessingChange(true);

    // URL del documento demo para preview
    const demoPreviewUrl = 'https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023.pdf';

    for (const file of fileArray) {
      if (file.type === 'application/pdf') {
        processedFiles.push({
          id: Date.now() + Math.random(),
          name: file.name,
          size: formatFileSize(file.size),
          type: file.type,
          file: file,
          previewUrl: demoPreviewUrl, // Usar siempre la URL del demo
          uploadedAt: new Date(),
          status: 'completed',
          documentType: targetKey || detectDocumentType(file.name)
        });
      }
    }

    // Simulate processing time
    setTimeout(() => {
      setIsProcessing(false);
      onProcessingChange && onProcessingChange(false);
      onFilesUploaded && onFilesUploaded([...uploadedFiles, ...processedFiles]);
    }, 2000); // Reducir tiempo de simulación
  };

  const detectDocumentType = (filename) => {
    const name = filename.toLowerCase();
    if (name.includes('balance') || name.includes('situacion') || name.includes('financiera')) {
      return 'balance';
    } else if (name.includes('resultado') || name.includes('perdida') || name.includes('ganancia')) {
      return 'income';
    } else if (name.includes('nota')) {
      return 'notes';
    }
    return 'unknown';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e, targetKey = null) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files, targetKey);
    }
  };

  const handleFileSelect = (e, targetKey = null) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files, targetKey);
    }
  };

  const removeFile = (fileId) => {
    const updatedFiles = uploadedFiles.filter(f => f.id !== fileId);
    onFilesUploaded && onFilesUploaded(updatedFiles);
  };

  const openPreview = (file) => {
    if (onShowPreview) {
      onShowPreview();
    }
  };

  const getUploadedFileForType = (documentType) => {
    return uploadedFiles.find(file => file.documentType === documentType);
  };

  const allDocumentsUploaded = requiredDocuments.every(doc => 
    uploadedFiles.some(file => file.documentType === doc.key)
  );

  return (
    <div style={{ padding: '24px', width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white', padding: '12px', borderRadius: '12px' }}>
          <Upload size={24} />
        </div>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
            Sube los estados financieros
          </h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '16px' }}>
            Carga los documentos PDF requeridos para el análisis
          </p>
        </div>
      </div>

      {/* Document Upload Zones */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        {requiredDocuments.map((doc) => {
          const uploadedFile = getUploadedFileForType(doc.key);
          const Icon = doc.icon;
          
          return (
            <div key={doc.key} style={{ position: 'relative' }}>
              <motion.div
                className={`upload-dropzone ${uploadedFile ? 'uploaded' : ''}`}
                style={{
                  border: uploadedFile ? '2px solid #10b981' : '2px dashed #d1d5db',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: uploadedFile ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)' : '#fafafa',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px'
                }}
                whileHover={{ scale: uploadedFile ? 1 : 1.02 }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={(e) => handleDrop(e, doc.key)}
                onClick={() => {
                  if (!uploadedFile) {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.pdf';
                    input.onchange = (e) => handleFileSelect(e, doc.key);
                    input.click();
                  }
                }}
              >
                <div style={{ 
                  color: uploadedFile ? '#10b981' : '#6b7280', 
                  marginBottom: '8px' 
                }}>
                  {uploadedFile ? <CheckCircle size={48} /> : <Icon size={48} />}
                </div>
                
                <h3 style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: '18px', 
                  fontWeight: '600',
                  color: uploadedFile ? '#065f46' : '#1f2937'
                }}>
                  {doc.name}
                </h3>
                
                <p style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '14px', 
                  color: uploadedFile ? '#047857' : '#6b7280'
                }}>
                  {doc.description}
                </p>

                {uploadedFile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                      background: '#10b981', 
                      color: 'white', 
                      padding: '4px 12px', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      fontWeight: '600'
                    }}>
                      ✓ Archivo cargado
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#047857' }}>
                      {uploadedFile.name}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openPreview(uploadedFile);
                        }}
                        style={{
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Eye size={14} />
                        Ver
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(uploadedFile.id);
                        }}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <X size={14} />
                        Quitar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#6b7280', fontSize: '14px' }}>
                    Arrastra el archivo aquí o haz clic para seleccionar
                  </div>
                )}
              </motion.div>
            </div>
          );
        })}
      </div>



      {/* Start Analysis Button */}
      {allDocumentsUploaded && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid #e5e7eb'
          }}
        >
          <button
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
            }}
          >
            <Play size={20} />
            Iniciar Análisis
          </button>
        </motion.div>
      )}

      {/* Processing Indicator */}
      {isProcessing && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '12px',
          padding: '20px',
          background: '#f3f4f6',
          borderRadius: '12px',
          marginTop: '20px'
        }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Clock size={20} style={{ color: '#3b82f6' }} />
          </motion.div>
          <span style={{ color: '#1f2937', fontWeight: '600' }}>
            Procesando archivos...
          </span>
        </div>
      )}


    </div>
  );
};

export default DocumentUpload; 